import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, useTheme } from "@mui/material";
import { Check } from "@mui/icons-material";

const StepIndicator = ({ steps, currentStep, onStepClick, completedSteps = [] }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const colors = {
    activeBg: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)",
    completedBg: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    inactiveBg: isDarkMode ? "#334155" : "#e2e8f0",
    activeText: "#ffffff",
    completedText: "#ffffff",
    inactiveText: isDarkMode ? "#94a3b8" : "#64748b",
    labelActive: isDarkMode ? "#f1f5f9" : "#1e293b",
    labelInactive: isDarkMode ? "#64748b" : "#94a3b8",
    connectorActive: "linear-gradient(90deg, #F97316, #EA580C)",
    connectorCompleted: "linear-gradient(90deg, #10b981, #059669)",
    connectorInactive: isDarkMode ? "#334155" : "#e2e8f0",
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: 0,
        mb: 4,
        px: 2,
        overflowX: "auto",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {steps.map((step, index) => {
        const isActive = index === currentStep;
        const isCompleted = completedSteps.includes(index) || index < currentStep;
        const isClickable = index <= currentStep || completedSteps.includes(index);

        return (
          <React.Fragment key={step.id}>
            {/* Step Circle + Label */}
            <Box
              component={motion.div}
              whileHover={isClickable ? { scale: 1.05 } : {}}
              whileTap={isClickable ? { scale: 0.95 } : {}}
              onClick={() => isClickable && onStepClick(index)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: isClickable ? "pointer" : "default",
                minWidth: "80px",
              }}
            >
              {/* Circle */}
              <Box
                component={motion.div}
                initial={false}
                animate={{
                  background: isCompleted
                    ? colors.completedBg
                    : isActive
                    ? colors.activeBg
                    : colors.inactiveBg,
                  boxShadow: isActive
                    ? "0 4px 14px rgba(234, 88, 12, 0.4)"
                    : isCompleted
                    ? "0 4px 14px rgba(16, 185, 129, 0.3)"
                    : "none",
                }}
                transition={{ duration: 0.3 }}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {isCompleted && !isActive ? (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  >
                    <Check sx={{ color: colors.completedText, fontSize: 22 }} />
                  </motion.div>
                ) : (
                  <Typography
                    component={motion.span}
                    initial={false}
                    animate={{
                      color: isActive ? colors.activeText : colors.inactiveText,
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    {index + 1}
                  </Typography>
                )}

                {/* Pulse animation for active step */}
                {isActive && (
                  <Box
                    component={motion.div}
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{
                      scale: [1, 1.4, 1],
                      opacity: [0.5, 0, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      background: colors.activeBg,
                      zIndex: -1,
                    }}
                  />
                )}
              </Box>

              {/* Label */}
              <Typography
                component={motion.span}
                initial={false}
                animate={{
                  color: isActive ? colors.labelActive : colors.labelInactive,
                  fontWeight: isActive ? 600 : 400,
                }}
                transition={{ duration: 0.2 }}
                sx={{
                  mt: 1,
                  fontSize: "0.75rem",
                  textAlign: "center",
                  maxWidth: "90px",
                  lineHeight: 1.3,
                }}
              >
                {step.label}
              </Typography>
            </Box>

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: "40px",
                  maxWidth: "80px",
                  height: "3px",
                  mt: "20px",
                  mx: 1,
                  borderRadius: "2px",
                  overflow: "hidden",
                  background: colors.connectorInactive,
                  position: "relative",
                }}
              >
                <Box
                  component={motion.div}
                  initial={false}
                  animate={{
                    width: isCompleted ? "100%" : "0%",
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  sx={{
                    height: "100%",
                    background: colors.connectorCompleted,
                    borderRadius: "2px",
                  }}
                />
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default StepIndicator;
